<template>
  <div
    v-if="code && user"
  >
    <v-card
      outlined
      :to="`/user/${user.name}/code/${code.id}`"
    >
      <v-card-title>
        <v-progress-circular
          :size="20"
          color="primary"
          indeterminate
          v-if="code.testing"
        ></v-progress-circular>
        <v-icon
          color="error"
          v-else-if="!code.pass"
        >mdi-alert-circle</v-icon>
        <v-icon
          color="success"
          v-else
        >mdi-checkbox-marked-circle</v-icon>

        <span class="mx-2">
          {{ challenge ? challenge.name : (code.challengeName ? code.challengeName : "非公開にされたチャレンジ") }}
        </span>
        <v-spacer />
        <span class="text-subtitle-1">
          {{ submittedAt }}
        </span>
      </v-card-title>
      <v-card-subtitle>
      </v-card-subtitle>
      <v-card-text>
        <v-simple-table dense>
          <template v-slot:default>
            <tbody>
              <tr>
                <th>結果</th>
                <td colspan="3">
                <span>{{ message }}</span>
                </td>
              </tr>
              <tr>
                <th :class="{ 'primary--text': highlight == 'ff' }">FF</th>
                <td :class="{ 'primary--text': highlight == 'ff' }">{{ code.ff }}</td>
                <th :class="{ 'primary--text': highlight == 'clock_period' }">周期</th>
                <td :class="{ 'primary--text': highlight == 'clock_period' }">{{ format_time(code.clock_period, 3) }}</td>
              </tr>
              <tr>
                <th :class="{ 'primary--text': highlight == 'lut' }">LUT</th>
                <td :class="{ 'primary--text': highlight == 'lut' }">{{ code.lut }}</td>
                <th :class="{ 'primary--text': highlight == 'clock_frequency' }">周波数</th>
                <td :class="{ 'primary--text': highlight == 'clock_frequency' }">{{ clock_frequency }}</td>
              </tr>
              <tr>
                <th :class="{ 'primary--text': highlight == 'dsp' }">DSP</th>
                <td :class="{ 'primary--text': highlight == 'dsp' }">{{ code.dsp }}</td>
                <th :class="{ 'primary--text': highlight == 'sim_cycle' }">サイクル</th>

                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">                    
                    <td :class="{ 'primary--text': highlight == 'sim_cycle' }" v-bind="attrs" v-on="on">
                      {{ format_cycle(code.sim_cycle, 2) }}
                    </td>
                  </template>
                  <span>{{ code.sim_cycle }}</span>
                </v-tooltip>
              </tr>
              <tr>
                <th :class="{ 'primary--text': highlight == 'bram' }">BRAM</th>
                <td :class="{ 'primary--text': highlight == 'bram' }">{{ code.bram }}</td>
                <th :class="{ 'primary--text': highlight == 'sim_time' }">タイム</th>
                
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">                    
                    <td :class="{ 'primary--text': highlight == 'sim_time' }" v-bind="attrs" v-on="on">
                      {{ format_time(code.sim_time, 2) }}
                    </td>
                  </template>
                  <span>{{ code.sim_time }}</span>
                </v-tooltip>
              </tr>
              <tr>
                <th :class="{ 'primary--text': highlight == 'uram' }">URAM</th>
                <td :class="{ 'primary--text': highlight == 'uram' }">{{ code.uram }}</td>
                <th></th>
                <td></td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
      <v-card-actions>
        <v-list-item class="grow">
          <v-list-item-avatar size="28">
            <img :src="user.photoURL" :alt="user.name">
          </v-list-item-avatar>
          <v-list-item-content>
            {{ user.name }}
          </v-list-item-content>

          <v-row
            align="center"
            justify="end"
          >
            <v-icon :color="likeColor" class="mr-2">mdi-thumb-up</v-icon>
            <span class="subheading mr-1">{{ likeCount }}</span>
          </v-row>
        </v-list-item>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import {db} from "@/firebase/db.js"

export default {
  props: ['code', 'highlight'], 
  data: () => ({
    user: null,
    challenge: null,
  }),
  computed: {
    submittedAt: function () {
      return this.code.submittedAt.toDate().toLocaleString('ja-JP')
    },
    likeCount: function () {
      return this.code.likes ? this.code.likes.length : 0
    },
    clock_frequency: function () {
      return this.code.clock_frequency ? this.code.clock_frequency.toFixed(1) : ""
    },
    likeColor: function () {
      const uid = this.$store.state.user.uid
      const likes = this.code.likes
      return this.$store.state.user.authenticated && likes && likes.includes(uid) ?
        "primary" : "grey"
    },
    message: function () {
      const test_steps = [
        { name: "csim",  message: "CSim実行中..." },
        { name: "hls",   message: "HLS実行中..." },
        { name: "cosim", message: "CoSim実行中..." },
        { name: "syn",   message: "論理合成中..." },
      ]

      if (!this.code) return ""
      if (this.code.pass || this.code.state == "tested") return this.code.message

      let message = "採点待ち..."
      if (this.code.testing) message = "採点中..."
      for (const step of test_steps) {
        if (this.code.state.includes(`testing_${step.name}`)) {
          message = step.message
          break
        }
      }
      return message
    }
  },
  methods: {
    bind: function () {
      this.$bind("user", db.collection("users").doc(this.code.user))
      this.$bind("challenge", db.doc(this.code.challengePath))
    },
    format_cycle: function(num, digits) {
      if (num === undefined) return ""
      const lookup = [
        { value: 1, symbol: "" },
        { value: 1e3, symbol: "k" },
        { value: 1e6, symbol: "M" },
        { value: 1e9, symbol: "G" },
        { value: 1e12, symbol: "T" },
        { value: 1e15, symbol: "P" },
        { value: 1e18, symbol: "E" }
      ];
      const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
      var item = lookup.slice().reverse().find(function(item) {
        return num >= item.value;
      });
      return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
    },
    format_time: function(num, digits) {
      if (num === undefined) return ""
      const lookup = [
        { value: 1, symbol: "ns" },
        { value: 1e3, symbol: "us" },
        { value: 1e6, symbol: "ms" },
        { value: 1e9, symbol: "s" },
      ];
      const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
      var item = lookup.slice().reverse().find(function(item) {
        return num >= item.value;
      });
      return item ? (num / item.value).toFixed(digits).replace(rx, "$1") + item.symbol : "0";
    },
  },
  watch: {
    code: function () {
      this.bind()
    },
  },
  created() {
    this.bind()
  }
};
</script>

